import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spinner, Table, Row, Col, Form, Badge } from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';

// -- helpers --
const toDate = (ts) =>
  ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);

const ageInDays = (d) => (d ? Math.floor((new Date() - d) / (1000 * 60 * 60 * 24)) : null);

const stockDisplay = (v) =>
  v?.strStockNbr
    ? v.strStockNbr
    : v?.strVin?.length >= 8
      ? v.strVin.slice(-8)
      : (v?.strVin || '');

const resolveModelName = (v, modelCodes = {}) => {
  const factory = (v?.strFactory ?? '').trim();
  const model = (v?.strModel ?? '').trim();
  if (!factory && !model) return '';
  const exact = `${factory} - ${model}`;
  const short = `${factory} - ${model.substring(0, 3)}`;
  return modelCodes[exact] || modelCodes[short] || model || '';
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

// -- component --
export default function StockAgeBuckets() {
  const { onSnapshotQueryData, getData } = useFirestore();
  const [vehicles, setVehicles] = useState([]);
  const [modelCodes, setModelCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [factory, setFactory] = useState('Ford'); // dropdown selection

  // live: only Stock vehicles
  useEffect(() => {
    const unsub = onSnapshotQueryData(
      'vehicles',
      'strSalesOrdNbr', '==', 'Stock',
      (data) => {
        setVehicles(data || []);
        setLoading(false);
      }
    );
    return () => unsub && unsub();
  }, [onSnapshotQueryData]);

  // model code names
  useEffect(() => {
    (async () => {
      try {
        const data = await getData('modelCodes');
        const map = (data || []).reduce((acc, obj) => {
          acc[`${obj.strFactory} - ${obj.strModel}`] = obj.strModelName;
          return acc;
        }, {});
        setModelCodes(map);
      } catch {/* ignore */ }
    })();
  }, [getData]);

  // compute buckets
  const buckets = useMemo(() => {
    const out = { gt180: [], d91_180: [], d31_90: [] };
    const sel = (factory || '').toLowerCase();

    (vehicles || []).forEach((v) => {
      const recv = toDate(v.dtRecvDate);
      if (!recv) return;

      // factory filter (treat exact includes)
      const vFactory = (v.strFactory || '').toLowerCase();
      const isFord = vFactory.includes('ford');
      const isGM = vFactory.includes('gm');
      if ((sel === 'ford' && !isFord) || (sel === 'gm' && !isGM)) return;

      const age = ageInDays(recv);
      if (age == null || age <= 30) return; // hide <= 30

      const row = {
        id: v.id,
        stock: stockDisplay(v),
        yr: v.strYR || '',
        model: resolveModelName(v, modelCodes),
        age,
        recv,
        vin: v.strVin || '',
      };

      if (age > 180) out.gt180.push(row);
      else if (age >= 91) out.d91_180.push(row);
      else if (age >= 31) out.d31_90.push(row);
    });

    // sort each bucket oldest first
    Object.values(out).forEach(arr => arr.sort((a, b) => b.age - a.age));
    return out;
  }, [vehicles, modelCodes, factory]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const BucketTable = ({ title, rows, variant }) => (
    <Card className="mb-4">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <span className="fw-bold">{title}</span>
        <Badge bg={variant || 'secondary'}>{rows.length}</Badge>
      </Card.Header>
      <Card.Body className="p-0">
        <Table responsive bordered hover className="mb-0">
          <thead>
            <tr>
              <th style={{ width: '18%' }}>Stock #</th>
              <th style={{ width: '12%' }}>Yr</th>
              <th>Model</th>
              <th style={{ width: '12%' }}>Age</th>
              <th style={{ width: '18%' }}>Received</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.stock}</td>
                <td>{r.yr}</td>
                <td>{r.model}</td>
                <td>{r.age}</td>
                <td>{fmtDate(r.recv)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted text-center">No vehicles in this bucket.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col xs="auto" className="fw-bold">Stock Vehicles by Age</Col>
        <Col xs={12} md="auto" className="mt-2 mt-md-0">
          <Form.Select
            value={factory}
            onChange={(e) => setFactory(e.target.value)}
            style={{ width: 200 }}
          >
            <option value="Ford">Ford</option>
            <option value="GM">GM</option>
          </Form.Select>
        </Col>
      </Row>

      <BucketTable title="> 180 Days" rows={buckets.gt180} variant="danger" />
      <BucketTable title="91 – 180 Days" rows={buckets.d91_180} variant="warning" />
      <BucketTable title="31 – 90 Days" rows={buckets.d31_90} variant="info" />
    </>
  );
}
